import { useState, useEffect } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, Pagination } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useClientFilter } from '../../hooks/useClientFilter'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import AlertaTimbres from '../common/AlertaTimbres'
import { formatCurrency } from '../../utils/formatters'
import {
  getDisplaySerieFolio,
  getInvoiceStatusBadge,
  isInvoiceCancelled,
} from '../../utils/invoiceDisplay'
import { formatDateOnlyEsMX, getDateOnlySortTimestamp } from '../../utils/apiDateFormat'
import {
  invoiceCanDelete,
  invoiceDeleteDisabledTitle,
  invoiceShowsDeleteButton,
} from '../../utils/invoicePermissions'
import { extractApiErrorMessage } from '../../utils/apiErrors'
import InvoiceForm from './InvoiceForm'
import InvoiceDetail from './InvoiceDetail'
import ApplyPaymentModal from './ApplyPaymentModal'
import BulkApplyPaymentModal from './BulkApplyPaymentModal'
import ComplementoPagoModal from './ComplementoPagoModal'
import NotaCreditoModal from './NotaCreditoModal'
import ProgramarRecurrencia from './ProgramarRecurrencia'

// Helper para obtener nombre del cliente
const getClientName = (client) => {
  if (client?.razon_social) return client.razon_social
  if (client?.business_name) return client.business_name
  const parts = []
  if (client?.nombre) parts.push(client.nombre)
  if (client?.apellido_paterno) parts.push(client.apellido_paterno)
  if (parts.length > 0) return parts.join(' ')
  return client?.name || 'N/A'
}

const InvoicesList = () => {
  const { user } = useAuth()
  const { clients } = useClientFilter()
  const {
    clientId: filterClient,
    setClientId: setFilterClient,
    selectDisabled,
  } = useSyncedLocalClientId()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [showApplyPayment, setShowApplyPayment] = useState(false)
  const [facturaARepetir, setFacturaARepetir] = useState(null)
  const [showBulkApplyPayment, setShowBulkApplyPayment] = useState(false)
  const [showComplementoPago, setShowComplementoPago] = useState(false)
  const [showNotaCredito, setShowNotaCredito] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [filterProcesado, setFilterProcesado] = useState('no')  // 'no' = no procesadas (default), 'si' = procesadas, 'all' = todas
  const [filterPpdAtencion, setFilterPpdAtencion] = useState(false)
  
  // Ordenamiento y paginación
  const [sortField, setSortField] = useState('issue_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  
  // Selección múltiple
  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [complementoDocInvoiceId, setComplementoDocInvoiceId] = useState(null)

  const { data: complementoDocData, isLoading: complementoDocLoading, error: complementoDocError } = useQuery(
    ['invoice-complementos-documentacion', complementoDocInvoiceId],
    async () => {
      const response = await api.get(`/financial/invoices/${complementoDocInvoiceId}/complementos-documentacion/`)
      return response.data
    },
    { enabled: !!complementoDocInvoiceId }
  )

  // Obtener lista de facturas
  const { data: invoicesData, isLoading, error } = useQuery(
    ['invoices', searchTerm, filterStatus, filterType, filterClient, filterProcesado, filterPpdAtencion],
    async () => {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (filterStatus !== 'all') params.append('status', filterStatus)
      if (filterType !== 'all') params.append('tipo_factura', filterType)
      if (filterClient) params.append('client_id', filterClient)
      if (filterProcesado !== 'all') params.append('procesado_contable', filterProcesado)
      if (filterPpdAtencion) params.append('ppd_atencion', '1')
      params.append('ordering', sortDirection === 'desc' ? `-${sortField}` : sortField)
      
      const response = await api.get(`/financial/invoices/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Estadísticas procesamiento (respeta filtros excepto procesado_contable)
  const { data: statsProcesamiento } = useQuery(
    ['invoices-stats-procesamiento', filterStatus, filterType, filterClient],
    async () => {
      const params = new URLSearchParams()
      if (filterStatus !== 'all') params.append('status', filterStatus)
      if (filterType !== 'all') params.append('tipo_factura', filterType)
      if (filterClient) params.append('client_id', filterClient)
      const response = await api.get(`/financial/invoices/estadisticas_procesamiento/?${params.toString()}`)
      return response.data
    },
    { enabled: !!user }
  )
  
  // Filtrar facturas localmente (si es necesario)
  const filteredInvoices = invoicesData || []
  
  // Ordenamiento local adicional (si es necesario)
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    
    if (sortField === 'issue_date' || sortField === 'due_date') {
      aVal = getDateOnlySortTimestamp(aVal)
      bVal = getDateOnlySortTimestamp(bVal)
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal?.toLowerCase() || ''
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    }
  })
  
  // Paginación
  const totalPages = Math.ceil(sortedInvoices.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedInvoices = sortedInvoices.slice(startIndex, endIndex)
  
  // Manejar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }
  
  // Indicador de ordenamiento
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted" style={{ fontSize: '0.75rem' }}></i>
    }
    return sortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-primary" style={{ fontSize: '0.75rem' }}></i>
      : <i className="bi bi-arrow-down text-primary" style={{ fontSize: '0.75rem' }}></i>
  }
  
  // Selección múltiple
  const toggleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    )
  }
  
  const toggleSelectAll = () => {
    if (selectedInvoices.length === paginatedInvoices.length && paginatedInvoices.length > 0) {
      setSelectedInvoices([])
    } else {
      setSelectedInvoices(paginatedInvoices.map(inv => inv.id))
    }
  }
  
  // Resetear paginación cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1)
  }, [filterStatus, filterType, filterClient, searchTerm, filterProcesado])
  
  // Resetear selección cuando cambia la página
  useEffect(() => {
    setSelectedInvoices([])
  }, [currentPage])

  // Mutación para crear/actualizar factura
  const invoiceMutation = useMutation(
    async (invoiceData) => {
      const clientId =
        invoiceData.client ||
        invoiceData.client_id ||
        selectedInvoice?.client ||
        selectedInvoice?.client_id

      // Cliente tiene prioridad. Si se edita en factura, se sincroniza al cliente.
      if (clientId && Object.prototype.hasOwnProperty.call(invoiceData, 'titulo_pdf')) {
        await api.patch(`/clients/clients/${clientId}/`, {
          nombre_comercial_pdf: invoiceData.titulo_pdf || ''
        })
      }

      if (invoiceData.id) {
        return api.put(`/financial/invoices/${invoiceData.id}/`, invoiceData)
      } else {
        return api.post('/financial/invoices/', invoiceData)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices')
        setShowForm(false)
        setSelectedInvoice(null)
        toast.success(selectedInvoice ? 'Factura actualizada exitosamente' : 'Factura creada exitosamente')
      },
      onError: (error) => {
        console.error('Error al guardar factura:', error.response?.data)
        let message = 'Error al guardar factura'
        
        if (error.response?.data) {
          const errorData = error.response.data
          
          // Si hay errores de validación del serializer (objeto con campos)
          if (typeof errorData === 'object' && !errorData.error && !errorData.message) {
            const errorMessages = []
            for (const [field, errors] of Object.entries(errorData)) {
              if (Array.isArray(errors)) {
                errorMessages.push(`${field}: ${errors.join(', ')}`)
              } else if (typeof errors === 'string') {
                errorMessages.push(`${field}: ${errors}`)
              } else if (typeof errors === 'object') {
                errorMessages.push(`${field}: ${JSON.stringify(errors)}`)
              }
            }
            message = errorMessages.length > 0 ? errorMessages.join('\n') : message
          } else {
            // Error simple o campo 'error'
            message = errorData.error || errorData.message || JSON.stringify(errorData)
          }
        }
        
        toast.error(message)
      }
    }
  )

  // Mutación para eliminar factura
  const deleteMutation = useMutation(
    async (invoiceId) => {
      return api.delete(`/financial/invoices/${invoiceId}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices')
        toast.success('Factura eliminada exitosamente')
      },
      onError: (error) => {
        toast.error(extractApiErrorMessage(error, 'Error al eliminar factura'))
      }
    }
  )

  // Mutación para clonar factura
  const clonarMutation = useMutation(
    async (invoiceId) => {
      const response = await api.post(`/financial/invoices/${invoiceId}/clonar/`)
      return response.data
    },
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
        toast.success('Factura clonada exitosamente')
        setSelectedInvoice(data.factura)
        setShowDetail(true)
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Error al clonar la factura'
        toast.error(message)
      }
    }
  )

  // Mutación para procesar contablemente
  const procesarContableMutation = useMutation(
    async (invoiceId) => {
      return api.post(`/financial/invoices/${invoiceId}/procesar_contable/`)
    },
    {
      onSuccess: (_data) => {
        queryClient.invalidateQueries('invoices')
        queryClient.invalidateQueries('invoices-stats-procesamiento')
        queryClient.invalidateQueries('transactions')
        toast.success('Factura procesada contablemente exitosamente')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al procesar contablemente')
      }
    }
  )

  // Mutación para revertir proceso contable
  const revertirProcesoContableMutation = useMutation(
    async (invoiceId) => api.post(`/financial/invoices/${invoiceId}/revertir_proceso_contable/`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices')
        queryClient.invalidateQueries('invoices-stats-procesamiento')
        queryClient.invalidateQueries('transactions')
        toast.success('Proceso contable revertido correctamente')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al revertir. No se puede revertir si la factura tiene pagos aplicados.')
      }
    }
  )

  const handleCreate = () => {
    setSelectedInvoice(null)
    setShowForm(true)
  }

  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice)
    setShowForm(true)
  }

  const handleView = (invoice) => {
    setSelectedInvoice(invoice)
    setShowDetail(true)
  }

  const handleDelete = (invoice) => {
    if (window.confirm(`¿Estás seguro de eliminar la factura ${getDisplaySerieFolio(invoice.invoice_number)}?`)) {
      deleteMutation.mutate(invoice.id)
    }
  }

  const handleProcesarContable = (invoice) => {
    if (window.confirm(`¿Procesar contablemente la factura ${getDisplaySerieFolio(invoice.invoice_number)}? Esto creará transacciones contables.`)) {
      procesarContableMutation.mutate(invoice.id)
    }
  }

  const handleRevertirProcesoContable = (invoice) => {
    if (window.confirm(`¿Revertir el proceso contable de la factura ${getDisplaySerieFolio(invoice.invoice_number)}? No se puede revertir si tiene pagos aplicados.`)) {
      revertirProcesoContableMutation.mutate(invoice.id)
    }
  }

  const handleSubmit = (formData) => {
    invoiceMutation.mutate(formData)
  }

  const getStatusBadge = (status) => {
    const badge = getInvoiceStatusBadge(status)
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getPagoEstadoBadge = (estado) => {
    const badges = {
      pendiente: { bg: 'warning', text: 'Pendiente' },
      aplicado: { bg: 'success', text: 'Aplicado' },
      cancelado: { bg: 'danger', text: 'Cancelado' },
    }
    const key = (estado || '').toLowerCase()
    const badge = badges[key] || { bg: 'secondary', text: estado || '—' }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const paymentMethodLabel = (code) => {
    const map = {
      CASH: 'Efectivo',
      CHECK: 'Cheque',
      TRANSFER: 'Transferencia',
      CARD: 'Tarjeta',
      OTHER: 'Otro',
    }
    return map[code] || code || '—'
  }

  const getTypeBadge = (type) => {
    const badges = {
      'normal': { bg: 'primary', text: 'Normal' },
      'global': { bg: 'info', text: 'Global' },
      'complemento': { bg: 'warning', text: 'Complemento' }
    }
    const badge = badges[type] || { bg: 'secondary', text: type }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getEstadoProcesamientoBadge = (invoice) => {
    const contable = invoice.is_processed_contablemente ? 'Procesado' : 'Pendiente'
    const contableBg = invoice.is_processed_contablemente ? 'success' : 'warning'
    return <Badge bg={contableBg} title="Estado contable">{contable}</Badge>
  }

  const getEstatusEmailBadge = (invoice) => {
    if (!invoice.cfdi_uuid) return <span className="text-muted">—</span>
    const estadoEmail = invoice.estado_email || 'pendiente'
    const labels = { enviado: 'Enviado', pendiente: 'Pendiente', error: 'Error' }
    const bgs = { enviado: 'success', pendiente: 'secondary', error: 'danger' }
    return (
      <Badge bg={bgs[estadoEmail] || 'secondary'}>{labels[estadoEmail] || estadoEmail}</Badge>
    )
  }

  const getAmbientePACBadge = (invoice) => {
    if (!invoice.cfdi_uuid) return <span className="text-muted">—</span>
    const amb = invoice.ambiente_timbrado || null
    if (amb === 'pruebas') return <Badge bg="warning">Pruebas</Badge>
    if (amb === 'produccion') return <Badge bg="success">Producción</Badge>
    return <span className="text-muted">—</span>
  }

  const getCuentasContablesCell = (invoice) => {
    const conCuenta = invoice.conceptos_con_cuenta ?? 0
    const sinCuenta = invoice.conceptos_sin_cuenta ?? 0
    return (
      <span title={`${conCuenta} con cuenta / ${sinCuenta} sin cuenta`}>
        <span className="text-success fw-bold">{conCuenta}</span>
        <span className="text-muted">/</span>
        <span className="text-danger fw-bold">{sinCuenta}</span>
      </span>
    )
  }

  // Estadísticas
  const stats = {
    total: filteredInvoices.length,
    draft: filteredInvoices.filter(inv => inv.status === 'DRAFT').length,
    sent: filteredInvoices.filter(inv => inv.status === 'SENT').length,
    paid: filteredInvoices.filter(inv => inv.status === 'PAID').length,
    partial: filteredInvoices.filter(inv => inv.status === 'PARTIAL').length,
    cancelPending: filteredInvoices.filter(inv => inv.status === 'CANCEL_PENDING').length,
    cancelled: filteredInvoices.filter(inv => inv.status === 'CANCELLED').length,
    totalAmount: filteredInvoices.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0),
    pendingAmount: filteredInvoices.reduce((sum, inv) => sum + parseFloat(inv.monto_pendiente || 0), 0),
    procesados: statsProcesamiento?.procesados ?? 0,
    noProcesados: statsProcesamiento?.no_procesados ?? 0
  }

  return (
    <div>
      {/* Antes que nada: si le quedan pocos timbres, que lo sepa antes de capturar una factura
          y que el PAC la rechace. */}
      <AlertaTimbres />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3>Facturas</h3>
          <p className="text-muted mb-0">
            <small>Total: {stats.total} | Pendiente: {formatCurrency(stats.pendingAmount)}</small>
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => setShowNotaCredito(true)}>
            Crear Nota de Crédito
          </Button>
          <Button variant="primary" onClick={handleCreate}>
            + Nueva Factura
          </Button>
        </div>
      </div>
      
      {/* Estadísticas rápidas */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center border-primary">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-primary">{stats.total}</h5>
              <small className="text-muted">Total</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-secondary">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-secondary">{stats.draft}</h5>
              <small className="text-muted">Borradores</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-info">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-info">{stats.sent}</h5>
              <small className="text-muted">Enviadas</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-success">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-success">{stats.paid}</h5>
              <small className="text-muted">Pagadas</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-warning">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-warning">{stats.partial}</h5>
              <small className="text-muted">Parciales</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-danger">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-danger">{stats.cancelled}</h5>
              <small className="text-muted">Canceladas</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className={`text-center ${filterProcesado === 'no' ? 'border-warning border-2' : 'border-warning'}`} style={{ cursor: 'pointer' }} onClick={() => setFilterProcesado('no')}>
            <Card.Body className="py-2">
              <h5 className="mb-0 text-warning">{stats.noProcesados}</h5>
              <small className="text-muted">Sin procesar</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className={`text-center ${filterProcesado === 'si' ? 'border-success border-2' : 'border-success'}`} style={{ cursor: 'pointer' }} onClick={() => setFilterProcesado('si')}>
            <Card.Body className="py-2">
              <h5 className="mb-0 text-success">{stats.procesados}</h5>
              <small className="text-muted">Procesadas</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Switch y Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Buscar por número, cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Todos los estados</option>
                <option value="DRAFT">Borrador</option>
                <option value="SENT">Enviada</option>
                <option value="PAID">Pagada</option>
                <option value="PARTIAL">Parcial</option>
                <option value="CANCEL_PENDING">Cancelación en proceso</option>
                <option value="CANCELLED">Cancelada</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Todos los tipos</option>
                <option value="normal">Normal</option>
                <option value="global">Global</option>
                <option value="complemento">Complemento</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                disabled={selectDisabled}
                title={selectDisabled ? 'Controlado por el filtro global del encabezado' : undefined}
              >
                <option value="">Todos los clientes</option>
                {(clients || []).map((client) => (
                  <option key={client.id} value={String(client.id)}>
                    {getClientName(client)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterProcesado}
                onChange={(e) => setFilterProcesado(e.target.value)}
                title="Vista: no procesadas (por defecto), procesadas o todas"
              >
                <option value="no">Sin procesar</option>
                <option value="si">Procesadas</option>
                <option value="all">Todas</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Check
                type="switch"
                id="filter-ppd-atencion"
                label="PPD pendientes"
                checked={filterPpdAtencion}
                onChange={(e) => setFilterPpdAtencion(e.target.checked)}
                title="PPD timbradas con saldo o pagos sin complemento (REP)"
              />
            </Col>
            <Col md={2} className="text-end">
              <Badge bg="secondary">Total: {filteredInvoices.length}</Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de facturas */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">
          Error al cargar facturas. Por favor, intenta nuevamente.
        </Alert>
      ) : sortedInvoices.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-muted">No se encontraron facturas</p>
            <Button variant="primary" onClick={handleCreate}>
              Crear primera factura
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            {/* Barra de acciones masivas */}
            {selectedInvoices.length > 0 && (
              <Card className="mb-3 border-primary">
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <strong>{selectedInvoices.length}</strong> factura(s) seleccionada(s)
                    </span>
                    <div>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={async () => {
                          if (window.confirm(`¿Cancelar ${selectedInvoices.length} factura(s)?`)) {
                            try {
                              const response = await api.post('/financial/invoices/bulk_action/', {
                                action: 'cancel',
                                invoice_ids: selectedInvoices
                              })
                              toast.success(response.data.message)
                              queryClient.invalidateQueries('invoices')
                              setSelectedInvoices([])
                            } catch (error) {
                              toast.error('Error al cancelar facturas')
                            }
                          }
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-2"
                        onClick={async () => {
                          if (window.confirm(`¿Procesar contablemente ${selectedInvoices.length} factura(s)?`)) {
                            try {
                              const response = await api.post('/financial/invoices/bulk_action/', {
                                action: 'procesar_contable',
                                invoice_ids: selectedInvoices
                              })
                              toast.success(response.data.message)
                              if (response.data?.errors?.length) {
                                response.data.errors.forEach(e => toast.warning(e))
                              }
                              queryClient.invalidateQueries('invoices')
                              queryClient.invalidateQueries('invoices-stats-procesamiento')
                              queryClient.invalidateQueries('transactions')
                              setSelectedInvoices([])
                            } catch (error) {
                              toast.error(error.response?.data?.error || 'Error al procesar contablemente')
                            }
                          }
                        }}
                      >
                        Procesar Contable
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="me-2"
                        onClick={async () => {
                          if (window.confirm(`¿Revertir proceso contable de ${selectedInvoices.length} factura(s)? No se puede revertir si tienen pagos aplicados.`)) {
                            try {
                              const response = await api.post('/financial/invoices/bulk_action/', {
                                action: 'revertir_proceso_contable',
                                invoice_ids: selectedInvoices
                              })
                              toast.success(response.data.message)
                              if (response.data?.errors?.length) {
                                response.data.errors.forEach(e => toast.warning(e))
                              }
                              queryClient.invalidateQueries('invoices')
                              queryClient.invalidateQueries('invoices-stats-procesamiento')
                              queryClient.invalidateQueries('transactions')
                              setSelectedInvoices([])
                            } catch (error) {
                              toast.error(error.response?.data?.error || 'Error al revertir')
                            }
                          }
                        }}
                      >
                        Revertir Proceso Contable
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="me-2"
                        onClick={() => setShowBulkApplyPayment(true)}
                        title="Aplicar pago por el total de cada factura seleccionada (solo las que tengan saldo pendiente)"
                      >
                        <i className="bi bi-cash-coin me-1"></i>
                        Pagos
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ms-2"
                        onClick={() => setSelectedInvoices([])}
                      >
                        Deseleccionar
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
            
            <Table responsive hover>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedInvoices.length === paginatedInvoices.length && paginatedInvoices.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Serie</th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('invoice_number')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Folio
                      {getSortIcon('invoice_number')}
                    </span>
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('client_name')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Cliente
                      {getSortIcon('client_name')}
                    </span>
                  </th>
                  <th>RFC Emisor</th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('receptor_name')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Receptor
                      {getSortIcon('receptor_name')}
                    </span>
                  </th>
                  <th>RFC Receptor</th>
                  <th>Tipo</th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('issue_date')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Fecha Emisión
                      {getSortIcon('issue_date')}
                    </span>
                  </th>
                  <th>Metodo pago</th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('total')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Total
                      {getSortIcon('total')}
                    </span>
                  </th>
                  <th>Pagado</th>
                  <th>Pendiente</th>
                  <th style={{ whiteSpace: 'pre-line' }}>C. Contables{'\n'}Encontradas</th>
                  <th>Estado Procesamiento</th>
                  <th>Estatus Email</th>
                  <th>Ambiente PAC</th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('status')}
                    className="position-relative"
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      Status Pago
                      {getSortIcon('status')}
                    </span>
                  </th>
                  <th style={{ minWidth: '200px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvoices.map((invoice) => (
                  <tr 
                    key={invoice.id}
                    style={{ backgroundColor: selectedInvoices.includes(invoice.id) ? '#e7f3ff' : 'transparent' }}
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => toggleSelectInvoice(invoice.id)}
                      />
                    </td>
                    <td>{invoice.configuracion_serie || '—'}</td>
                    <td>{getDisplaySerieFolio(invoice.invoice_number)}</td>
                    <td>
                      {invoice.client_name || getClientName(invoice.client) || 'N/A'}
                    </td>
                    <td className="text-nowrap">
                      {invoice.client_rfc || invoice.client?.rfc || '—'}
                    </td>
                    <td>
                      {invoice.receptor_name || 'N/A'}
                    </td>
                    <td className="text-nowrap">
                      {invoice.receptor_rfc || invoice.receptor_rfc_display || '—'}
                    </td>
                    <td>{getTypeBadge(invoice.tipo_factura)}</td>
                    <td>{invoice.issue_date ? formatDateOnlyEsMX(invoice.issue_date, {}, 'N/A') : 'N/A'}</td>
                    <td>
                      <div className="d-flex flex-column align-items-start gap-1">
                        <small title={invoice.metodo_pago === 'PUE' ? 'Pago en una sola exhibición' : invoice.metodo_pago === 'PPD' ? 'Pago en parcialidades o diferido' : ''}>
                          {invoice.metodo_pago || '—'}
                        </small>
                        {invoice.metodo_pago === 'PPD' && invoice.documentada_en_complemento_timbrado && (
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none small"
                            onClick={() => setComplementoDocInvoiceId(invoice.id)}
                            title="Ver complementos de pago que documentan esta factura (timbrados)"
                          >
                            <Badge bg="info" className="fw-normal">Complemento</Badge>
                          </Button>
                        )}
                      </div>
                    </td>
                    <td>
                      {formatCurrency(invoice.total || 0)}
                    </td>
                    <td>
                      {formatCurrency(invoice.monto_pagado || 0)}
                    </td>
                    <td>
                      {formatCurrency(invoice.monto_pendiente || 0)}
                    </td>
                    <td>{getCuentasContablesCell(invoice)}</td>
                    <td>{getEstadoProcesamientoBadge(invoice)}</td>
                    <td>{getEstatusEmailBadge(invoice)}</td>
                    <td>{getAmbientePACBadge(invoice)}</td>
                    <td>{getStatusBadge(invoice.status)}</td>
                    <td style={{ whiteSpace: 'nowrap', minWidth: '200px' }}>
                      <div className="d-flex gap-2 justify-content-center align-items-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleView(invoice)}
                          title="Ver detalles"
                          style={{ minWidth: '80px' }}
                        >
                          <i className="bi bi-eye me-1"></i>
                          <span>Ver</span>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEdit(invoice)}
                          disabled={!!invoice.cfdi_uuid && !isInvoiceCancelled(invoice.status)}
                          title={(invoice.cfdi_uuid && !isInvoiceCancelled(invoice.status)) ? 'No se puede editar una factura timbrada' : 'Editar factura'}
                          style={{ minWidth: '80px' }}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          <span>Editar</span>
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => clonarMutation.mutate(invoice.id)}
                          disabled={clonarMutation.isLoading}
                          title="Clonar factura"
                          style={{ minWidth: '80px' }}
                        >
                          {clonarMutation.isLoading ? (
                            <><Spinner size="sm" className="me-1" /> Clonando...</>
                          ) : (
                            <><i className="bi bi-copy me-1"></i> Clonar</>
                          )}
                        </Button>
                        {/* Repetir: toma ESTA factura como modelo de una recurrencia. Clonar hace
                            una copia hoy; repetir programa que se haga sola cada periodo.
                            No se ofrece en complementos de pago --un REP no se repite-- ni en
                            canceladas. */}
                        {invoice.tipo_factura !== 'complemento' &&
                         !isInvoiceCancelled(invoice.status) && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => { setFacturaARepetir(invoice) }}
                            title="Repetir esta factura cada periodo"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-arrow-repeat me-1"></i> Repetir
                          </Button>
                        )}
                        {invoice.tipo_factura !== 'complemento' &&
                         !isInvoiceCancelled(invoice.status) &&
                         invoice.status !== 'DRAFT' &&
                         (parseFloat(invoice.monto_pendiente || 0) > 0.01 ||
                          parseFloat(invoice.monto_pagado || 0) > 0.01) && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                              setSelectedInvoice(invoice)
                              setShowApplyPayment(true)
                            }}
                            title={
                              parseFloat(invoice.monto_pendiente || 0) > 0.01
                                ? 'Aplicar pago o ver pagos asociados'
                                : 'Ver pagos asociados a esta factura'
                            }
                            style={{ minWidth: '90px' }}
                          >
                            <i className="bi bi-cash-coin me-1"></i>
                            <span>Pago</span>
                          </Button>
                        )}
                        {invoice.tipo_factura === 'normal' &&
                         invoice.metodo_pago === 'PPD' &&
                         invoice.cfdi_uuid &&
                         parseFloat(invoice.monto_pagado || 0) > 0.01 && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => {
                              setSelectedInvoice(invoice)
                              setShowComplementoPago(true)
                            }}
                            title="Generar complemento de pago (borrador)"
                            style={{ minWidth: '110px' }}
                          >
                            <i className="bi bi-receipt me-1"></i>
                            <span>Compl. pago</span>
                          </Button>
                        )}
                        {invoice.tipo_factura === 'normal' && invoice.status !== 'DRAFT' && !invoice.is_processed_contablemente && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleProcesarContable(invoice)}
                            disabled={procesarContableMutation.isLoading || !invoice.client}
                            title={!invoice.client ? "Asigna un cliente para procesar" : "Procesar contablemente"}
                            style={{ minWidth: '120px' }}
                          >
                            <i className="bi bi-calculator me-1"></i>
                            <span>Procesar Contable</span>
                          </Button>
                        )}
                        {invoice.tipo_factura === 'normal' && invoice.is_processed_contablemente && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleRevertirProcesoContable(invoice)}
                            disabled={revertirProcesoContableMutation.isLoading}
                            title="Revertir proceso contable. No se puede si tiene pagos aplicados."
                            style={{ minWidth: '140px' }}
                          >
                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                            <span>Revertir Proceso Contable</span>
                          </Button>
                        )}
                        {invoiceShowsDeleteButton(invoice) && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(invoice)}
                            disabled={deleteMutation.isLoading || !invoiceCanDelete(invoice)}
                            title={invoiceDeleteDisabledTitle(invoice)}
                            style={{ minWidth: '90px' }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            <span>Eliminar</span>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Paginación */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Mostrar:</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                  <span className="text-muted">
                    {startIndex + 1}-{Math.min(endIndex, sortedInvoices.length)} de {sortedInvoices.length}
                  </span>
                </div>
                
                <Pagination className="mb-0">
                  <Pagination.First 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  />
                  <Pagination.Prev 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  />
                  
                  {/* Números de página (máximo 5 visibles) */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    )
                  })}
                  
                  <Pagination.Next 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  />
                  <Pagination.Last 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario */}
      <Modal show={showForm} onHide={() => { setShowForm(false); setSelectedInvoice(null) }} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{selectedInvoice ? 'Editar Factura' : 'Nueva Factura'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InvoiceForm
            invoice={selectedInvoice}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setSelectedInvoice(null) }}
            isLoading={invoiceMutation.isLoading}
          />
        </Modal.Body>
      </Modal>

      {/* Modal de detalle */}
      <Modal show={showDetail} onHide={() => { setShowDetail(false); setSelectedInvoice(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedInvoice?.tipo_factura === 'complemento'
              ? 'Complemento de pago'
              : 'Detalle de la Factura'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <InvoiceDetail
              invoice={selectedInvoice}
              onEdit={() => {
                setShowDetail(false)
                handleEdit(selectedInvoice)
              }}
              onInvoiceUpdated={(updated) => setSelectedInvoice(updated)}
              onRefresh={async () => {
                // Invalidar todas las queries de facturas
                await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false })
                await queryClient.invalidateQueries(['invoice', selectedInvoice.id])
                
                // Refetch para actualizar la vista inmediatamente
                await queryClient.refetchQueries({ queryKey: ['invoices'], exact: false })
                
                // Pequeño delay para asegurar que los datos se actualicen antes de cerrar
                await new Promise(resolve => setTimeout(resolve, 200))
                
                // Cerrar el modal y limpiar la selección después de timbrar exitosamente
                // Esto permite que la lista se actualice y refleje el nuevo estado
                setShowDetail(false)
                setSelectedInvoice(null)
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!complementoDocInvoiceId}
        onHide={() => setComplementoDocInvoiceId(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Complementos de pago (documentación)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {complementoDocLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando…
            </div>
          )}
          {complementoDocError && (
            <Alert variant="danger">
              {complementoDocError.response?.data?.detail || complementoDocError.message || 'Error al cargar los datos'}
            </Alert>
          )}
          {!complementoDocLoading && !complementoDocError && complementoDocData && (
            <>
              {(!complementoDocData.rows || complementoDocData.rows.length === 0) ? (
                <p className="text-muted mb-0">No hay complementos timbrados que documenten esta factura.</p>
              ) : (
                <Table responsive striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Complemento</th>
                      <th>Estatus complemento</th>
                      <th>Pago</th>
                      <th>Estatus pago</th>
                      <th className="text-end">Monto aplicado</th>
                      <th className="text-center">Parcialidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complementoDocData.rows.map((row) => (
                      <tr key={row.aplicacion_id}>
                        <td>
                          <div className="small">
                            <strong>{row.complemento?.invoice_number || '—'}</strong>
                          </div>
                          <div className="text-muted small text-break" title={row.complemento?.cfdi_uuid}>
                            {row.complemento?.cfdi_uuid
                              ? `${row.complemento.cfdi_uuid.slice(0, 8)}…`
                              : '—'}
                          </div>
                        </td>
                        <td>{getStatusBadge(row.complemento?.status)}</td>
                        <td>
                          <div className="small">
                            <strong>#{row.pago?.id}</strong>
                            {row.pago?.payment_date != null && (
                              <span className="text-muted ms-1">
                                {formatDateOnlyEsMX(row.pago.payment_date, {}, '')}
                              </span>
                            )}
                          </div>
                          <div className="small text-muted">
                            {paymentMethodLabel(row.pago?.payment_method)}
                            {row.pago?.reference ? ` · Ref. ${row.pago.reference}` : ''}
                          </div>
                          <div className="small">
                            {formatCurrency(row.pago?.amount || 0)}{' '}
                            <span className="text-muted">(total pago)</span>
                          </div>
                        </td>
                        <td>{getPagoEstadoBadge(row.pago?.estado)}</td>
                        <td className="text-end">
                          {formatCurrency(row.monto_aplicado || 0)}
                        </td>
                        <td className="text-center">{row.num_parcialidad ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal de aplicar pago */}
      <ApplyPaymentModal
        show={showApplyPayment}
        onHide={() => {
          setShowApplyPayment(false)
          setSelectedInvoice(null)
        }}
        invoice={selectedInvoice}
        onSuccess={() => {
          queryClient.invalidateQueries('invoices')
          queryClient.invalidateQueries('payments')
          setShowApplyPayment(false)
          setSelectedInvoice(null)
        }}
      />

      {/* Programar que esta factura se repita. Lo que se factura sale de ella: aquí solo se
          configura cada cuánto. */}
      {facturaARepetir && (
        <ProgramarRecurrencia
          factura={facturaARepetir}
          onHide={() => setFacturaARepetir(null)}
          onSuccess={() => queryClient.invalidateQueries('recurrencias')}
        />
      )}

      {/* Modal de aplicar pago masivo */}
      <BulkApplyPaymentModal
        show={showBulkApplyPayment}
        onHide={() => {
          setShowBulkApplyPayment(false)
        }}
        items={sortedInvoices.filter(inv => selectedInvoices.includes(inv.id))}
        isBill={false}
        onSuccess={() => {
          queryClient.invalidateQueries('invoices')
          queryClient.invalidateQueries('payments')
          setShowBulkApplyPayment(false)
          setSelectedInvoices([])
        }}
      />

      <ComplementoPagoModal
        show={showComplementoPago}
        onHide={() => setShowComplementoPago(false)}
        invoice={selectedInvoice}
        onSuccess={async (complemento) => {
          queryClient.invalidateQueries('invoices')
          setShowComplementoPago(false)
          if (!complemento?.id) return
          try {
            const { data } = await api.get(`/financial/invoices/${complemento.id}/`)
            setSelectedInvoice(data)
          } catch {
            setSelectedInvoice(complemento)
          }
          setShowDetail(true)
        }}
      />

      <NotaCreditoModal
        show={showNotaCredito}
        onHide={() => setShowNotaCredito(false)}
        clients={clients || []}
        onCreated={(factura) => {
          setShowNotaCredito(false)
          if (factura?.id) {
            setSelectedInvoice(factura)
            setShowDetail(true)
          }
        }}
      />
    </div>
  )
}

export default InvoicesList

